/**
 * CoverageReportSection
 *
 * Displays knowledge coverage analysis showing what % of product attributes
 * have domain knowledge before content generation.
 *
 * Part of Story 50: Knowledge Coverage Scoring
 *
 * Date: December 23, 2025
 */

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { AlertCircle, CheckCircle, AlertTriangle, Loader2, RefreshCw, BookOpen, TrendingUp } from 'lucide-react';
import {
  CoverageReport,
  getCoverageReport,
  checkCoverage,
  getCoverageRiskColor,
  getCoveragePercentageColor,
} from '@/services/contentReviewService';

interface CoverageReportSectionProps {
  generationId?: string;
  productId?: string;
  siteId?: string;
  autoRun?: boolean;
}

export function CoverageReportSection({
  generationId,
  productId,
  siteId,
  autoRun = false,
}: CoverageReportSectionProps) {
  const [report, setReport] = useState<CoverageReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChecked, setHasChecked] = useState(false);

  // Load existing report on mount
  useEffect(() => {
    async function loadExistingReport() {
      if (!generationId) {
        if (autoRun && productId && siteId) {
          handleRunCheck();
        }
        return;
      }

      try {
        const response = await getCoverageReport(generationId);
        if (response.data) {
          setReport(response.data);
          setHasChecked(true);
        } else if (autoRun && productId && siteId) {
          handleRunCheck();
        }
      } catch (err) {
        console.error('Error loading coverage report:', err);
        if (autoRun && productId && siteId) {
          handleRunCheck();
        }
      }
    }

    loadExistingReport();
  }, [generationId, productId, siteId, autoRun]);

  async function handleRunCheck() {
    if (!productId || !siteId) {
      setError('Product ID and Site ID are required to check coverage');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await checkCoverage(productId, siteId);
      if (response.data) {
        setReport(response.data);
        setHasChecked(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check coverage');
    } finally {
      setLoading(false);
    }
  }

  // Render risk level icon
  const RiskIcon = () => {
    if (!report) return null;

    switch (report.riskLevel) {
      case 'LOW':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'MEDIUM':
        return <TrendingUp className="h-5 w-5 text-yellow-500" />;
      case 'HIGH':
        return <AlertTriangle className="h-5 w-5 text-orange-500" />;
      case 'CRITICAL':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      default:
        return null;
    }
  };

  // If no check has been run yet
  if (!hasChecked && !loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Knowledge Coverage Check
          </CardTitle>
          <CardDescription>
            Analyze what percentage of product attributes have domain knowledge
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={handleRunCheck} disabled={loading || !productId || !siteId}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Checking...
              </>
            ) : (
              'Check Coverage'
            )}
          </Button>
          {!productId || !siteId ? (
            <p className="mt-2 text-sm text-muted-foreground">
              Product ID and Site ID required for coverage check
            </p>
          ) : null}
          {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
        </CardContent>
      </Card>
    );
  }

  // Loading state
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            Checking Knowledge Coverage...
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Extracting product attributes and matching against domain knowledge...
          </p>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error && !report) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-500">
            <AlertCircle className="h-5 w-5" />
            Coverage Check Error
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-red-500 mb-4">{error}</p>
          <Button variant="outline" onClick={handleRunCheck}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Report display
  if (!report) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <RiskIcon />
            Knowledge Coverage
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant={getCoverageRiskColor(report.riskLevel)}>
              {report.riskLevel} RISK
            </Badge>
            {report.belowThreshold && (
              <Badge variant="destructive">Below Threshold</Badge>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRunCheck}
              disabled={loading || !productId || !siteId}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        <CardDescription>
          {report.coveragePercentage.toFixed(1)}% of attributes have domain knowledge
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Coverage Progress Bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className={getCoveragePercentageColor(report.coveragePercentage)}>
              {report.knownAttributes} of {report.totalAttributes} attributes covered
            </span>
            {report.requiredThreshold && (
              <span className="text-muted-foreground">
                Required: {report.requiredThreshold}%
              </span>
            )}
          </div>
          <Progress value={report.coveragePercentage} className="h-3" />
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-4 text-center">
          <div className="space-y-1">
            <p className="text-2xl font-bold">{report.totalAttributes}</p>
            <p className="text-xs text-muted-foreground">Total Attributes</p>
          </div>
          <div className="space-y-1">
            <p className="text-2xl font-bold text-green-600">{report.knownAttributes}</p>
            <p className="text-xs text-muted-foreground">Covered</p>
          </div>
          <div className="space-y-1">
            <p className="text-2xl font-bold text-red-600">{report.unknownAttributes.length}</p>
            <p className="text-xs text-muted-foreground">Unknown</p>
          </div>
        </div>

        {/* Category and Price Tier */}
        {(report.category || report.priceTier) && (
          <div className="flex gap-2">
            {report.category && (
              <Badge variant="outline">Category: {report.category}</Badge>
            )}
            {report.priceTier && (
              <Badge variant="outline">Tier: {report.priceTier}</Badge>
            )}
          </div>
        )}

        {/* Unknown Attributes */}
        {report.unknownAttributes.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-medium text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-500" />
              Missing Knowledge ({report.unknownAttributes.length})
            </h4>
            <div className="flex flex-wrap gap-2">
              {report.unknownAttributes.map((attr, index) => (
                <Badge key={index} variant="secondary" className="font-mono text-xs">
                  {attr}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Known Attributes (collapsed by default) */}
        {report.knownAttributeNames.length > 0 && (
          <details className="space-y-2">
            <summary className="cursor-pointer font-medium text-sm flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              Covered Attributes ({report.knownAttributeNames.length})
            </summary>
            <div className="mt-2 flex flex-wrap gap-2 pl-6">
              {report.knownAttributeNames.map((attr, index) => (
                <Badge key={index} variant="outline" className="font-mono text-xs text-green-700">
                  {attr}
                </Badge>
              ))}
            </div>
          </details>
        )}

        {/* Threshold Override Warning */}
        {report.thresholdOverride && (
          <div className="p-3 border rounded-lg bg-yellow-50 dark:bg-yellow-950/20">
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              Threshold override applied - generation proceeded despite low coverage
            </p>
          </div>
        )}

        {/* Timestamp */}
        <p className="text-xs text-muted-foreground text-right">
          Checked: {new Date(report.createdAt).toLocaleString()}
        </p>
      </CardContent>
    </Card>
  );
}
